import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ApproveButton from 'components/v2/buttons/ApproveButton';
import DeclineButton from 'components/v2/buttons/DeclineButton';
import TextField from 'components/v2/forms/TextField';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import AddButton from 'components/v2/buttons/AddButton';
import CustomDropdown from 'components/v2/dropdowns/CustomDropdown';
import { getReferenceNumbersMasterList, removeReferenceNumberFromMasterList, upsertReferenceNumberToMasterList, } from 'utils/UserServiceAPIUtils';
import { showToast } from 'stores/ToastStore';
import Spinner from 'components/ui/Spinner';
import Table from 'components/v2/Table';
import Pagination from 'components/v2/PaginationV2';
import { titleize } from 'underscore.string';
import USER_TYPES from 'constants/UserTypes';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import Panel from 'components/v2/Panel';
import { sluggify } from 'utils/localization/utils';
const ITEMS_PER_PAGE_LIMIT = 12;
const REFERENCE_NUMBER_MAX_LENGTH = 45;
const ReferenceNumbersMasterList = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [activePage, setActivePage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [referenceNumbers, setReferenceNumbers] = useState([]);
    const [newReferenceNumberSlug, setNewReferenceNumberSlug] = useState('');
    const [newReferenceNumberViewableBy, setNewReferenceNumberViewableBy] = useState([]);
    const [newReferenceNumberEditableBy, setNewReferenceNumberEditableBy] = useState([]);
    const [editReferenceNumberSlug, setEditReferenceNumberSlug] = useState('');
    const [editReferenceNumberViewableBy, setEditReferenceNumberViewableBy] = useState([]);
    const [editReferenceNumberEditableBy, setEditReferenceNumberEditableBy] = useState([]);
    const [referenceNumberInEdit, setReferenceNumberInEdit] = useState(null);
    const [canUpdate, setCanUpdate] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    useEffect(() => {
        fetchReferenceNumbersMasterList();
    }, [activePage, totalCount]);
    useEffect(() => {
        calculateCanAdd();
    }, [isSaving, newReferenceNumberSlug]);
    useEffect(() => {
        calculateCanUpdate();
    }, [
        isSaving,
        editReferenceNumberSlug,
        editReferenceNumberViewableBy,
        editReferenceNumberEditableBy,
    ]);
    const userTypeOptions = [
        {
            name: t('shipper'),
            value: USER_TYPES.SHIPPER,
        },
        {
            name: t('carrier'),
            value: USER_TYPES.CARRIER,
        },
    ];
    const breadcrumbs = {
        pages: [
            {
                name: t('REFERENCE_NUMBERS'),
                href: `/tools/reference-numbers`,
                current: true,
            },
        ],
    };
    async function fetchReferenceNumbersMasterList() {
        var _a;
        try {
            setIsLoading(true);
            const skip = activePage === 1 ? 0 : ITEMS_PER_PAGE_LIMIT * (activePage - 1);
            const response = await getReferenceNumbersMasterList(ITEMS_PER_PAGE_LIMIT, skip);
            const queryResult = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.getReferenceNumbersQuery;
            if (!queryResult) {
                throw new Error('No response from API');
            }
            const { reference_numbers, total } = queryResult;
            setReferenceNumbers(reference_numbers);
            setTotalCountAndCalculatePages(total);
        }
        catch (err) {
            showToast(t('ERROR_FETCHING_DATA'), null, 'error');
            console.error('Error while loading reference numbers master list', err.message);
        }
        finally {
            setIsLoading(false);
        }
    }
    async function removeReferenceNumber(referenceNumberId) {
        var _a, _b;
        try {
            const response = await removeReferenceNumberFromMasterList(referenceNumberId);
            const success = ((_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.removeReferenceNumbersMutation) === null || _b === void 0 ? void 0 : _b.success) === true;
            if (success) {
                const newReferenceNumbersList = referenceNumbers.filter((referenceNumber) => referenceNumber.id !== referenceNumberId);
                setReferenceNumbers(newReferenceNumbersList);
                showToast(t('REMOVE_REFERENCE_NUMBER_FROM_MASTER_LIST_SUCCESS'));
                setTotalCountAndCalculatePages(totalCount - 1);
                return;
            }
        }
        catch (err) {
            console.error(`Error while trying to remove reference number from master list`, err.message);
        }
        showToast(t('REMOVE_REFERENCE_NUMBER_FROM_MASTER_LIST_FAILED'), null, 'error');
    }
    async function upsertReferenceNumber(referenceNumber) {
        var _a;
        if (!referenceNumber)
            return;
        try {
            setIsSaving(true);
            if (isSaving)
                return;
            const response = await upsertReferenceNumberToMasterList(referenceNumber.slug, referenceNumber.viewableBy || [], referenceNumber.editableBy || [], referenceNumber.id);
            return (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.upsertReferenceNumbersMutation;
        }
        catch (err) {
            console.error(`Error while trying to save reference number ${JSON.stringify(referenceNumber)} to master list`, err.message);
            showToast(t('REFERENCE_NUMBER_MASTER_LIST_SAVE_FAILED'), null, 'error');
        }
        finally {
            setIsSaving(false);
        }
    }
    function sameArrayNoDuplicates(a, b) {
        return new Set(a).size === b.length && a.every((item) => b.includes(item));
    }
    function handlePageChange(page) {
        setActivePage(page);
    }
    function handleUserInputChangeForNewReferenceNumber(newValue) {
        setNewReferenceNumberSlug(newValue);
    }
    function enterEditMode(referenceNumber) {
        setReferenceNumberInEdit(referenceNumber);
        setEditReferenceNumberSlug(referenceNumber.slug);
        setEditReferenceNumberViewableBy(referenceNumber.viewable_by);
        setEditReferenceNumberEditableBy(referenceNumber.editable_by);
        setCanUpdate(false);
    }
    function handleRemove(referenceNumber) {
        removeReferenceNumber(referenceNumber.id);
    }
    async function handleFinishEdit() {
        const editReferenceNumberToUpdate = {
            slug: editReferenceNumberSlug,
            viewableBy: editReferenceNumberViewableBy,
            editableBy: editReferenceNumberEditableBy,
            id: referenceNumberInEdit.id,
        };
        const savedReferenceNumbers = await upsertReferenceNumber(editReferenceNumberToUpdate);
        const success = (savedReferenceNumbers === null || savedReferenceNumbers === void 0 ? void 0 : savedReferenceNumbers.length) === 1;
        if (success) {
            const savedReferenceNumber = savedReferenceNumbers[0];
            const copyReferenceNumbers = [...referenceNumbers];
            const indexOfOldReferenceNumber = copyReferenceNumbers.findIndex((referenceNumber) => referenceNumber.id === savedReferenceNumber.id);
            copyReferenceNumbers[indexOfOldReferenceNumber] = savedReferenceNumber;
            setReferenceNumbers(copyReferenceNumbers);
            exitEditMode();
            showToast(t('UPDATE_MASTER_LIST_REFERENCE_NUMBER_SUCCESS'));
        }
        else {
            showToast(t('UPDATE_MASTER_LIST_REFERENCE_NUMBER_FAILED'), null, 'error');
        }
    }
    function exitEditMode() {
        setReferenceNumberInEdit(undefined);
        setEditReferenceNumberSlug('');
        setEditReferenceNumberViewableBy([]);
        setEditReferenceNumberEditableBy([]);
    }
    const sluggifyForUpload = (slug) => {
        slug = sluggify(slug);
        return slug.toLowerCase();
    };
    async function handleAdd() {
        const newReferenceNumber = {
            slug: sluggifyForUpload(newReferenceNumberSlug),
            viewableBy: newReferenceNumberViewableBy,
            editableBy: newReferenceNumberEditableBy,
        };
        const savedReferenceNumbers = await upsertReferenceNumber(newReferenceNumber);
        const success = (savedReferenceNumbers === null || savedReferenceNumbers === void 0 ? void 0 : savedReferenceNumbers.length) === 1;
        if (success) {
            const newReferenceNumberList = [
                ...referenceNumbers,
                savedReferenceNumbers[0],
            ];
            setReferenceNumbers(newReferenceNumberList);
            setNewReferenceNumberSlug('');
            calculateCanAdd('');
            setTotalCountAndCalculatePages(totalCount + 1);
            showToast(t('ADD_REFERENCE_NUMBER_FROM_MASTER_LIST_SUCCESS'));
            setNewReferenceNumberSlug('');
            setNewReferenceNumberViewableBy([]);
            setNewReferenceNumberEditableBy([]);
        }
        else {
            showToast(t('ADD_REFERENCE_NUMBER_FROM_MASTER_LIST_FAILED'), null, 'error');
        }
    }
    function handleEditInputChange(newValue) {
        setEditReferenceNumberSlug(newValue);
    }
    function isInputValidAsReferenceNumber(userInput) {
        if (!userInput || userInput.length > REFERENCE_NUMBER_MAX_LENGTH) {
            return false;
        }
        return true;
    }
    function setTotalCountAndCalculatePages(total) {
        setTotalCount(total);
        const pages = total <= ITEMS_PER_PAGE_LIMIT
            ? 1
            : Math.ceil(total / ITEMS_PER_PAGE_LIMIT);
        setTotalPages(pages);
    }
    function isRowInEdit(row) {
        return (referenceNumberInEdit === null || referenceNumberInEdit === void 0 ? void 0 : referenceNumberInEdit.id) && row.id === referenceNumberInEdit.id;
    }
    function calculateCanUpdate() {
        if (!referenceNumberInEdit)
            return false;
        const slugChanged = referenceNumberInEdit.slug !== editReferenceNumberSlug;
        const slugValid = isInputValidAsReferenceNumber(editReferenceNumberSlug);
        const viewableByChanged = !sameArrayNoDuplicates(editReferenceNumberViewableBy, referenceNumberInEdit.viewable_by);
        const editableByChanged = !sameArrayNoDuplicates(editReferenceNumberEditableBy, referenceNumberInEdit.editable_by);
        const dataChanged = viewableByChanged || editableByChanged || slugChanged;
        const _canUpdate = !isSaving && dataChanged && slugValid;
        setCanUpdate(_canUpdate);
    }
    function calculateCanAdd() {
        const inputValid = isInputValidAsReferenceNumber(newReferenceNumberSlug);
        setCanAdd(inputValid && !isSaving);
    }
    function onViewableByForExistingReferenceNumberSelectionChanged(selectedViewableBy) {
        const selectedViewableUserTypes = selectedViewableBy.map((selectedViewableBy) => selectedViewableBy.value);
        setEditReferenceNumberViewableBy(selectedViewableUserTypes);
    }
    function onEditableByForExistingReferenceNumberSelectionChanged(selectedEditableBy) {
        const selectedEditableUserTypes = selectedEditableBy.map((selectedEditableBy) => selectedEditableBy.value);
        setEditReferenceNumberEditableBy(selectedEditableUserTypes);
    }
    function onViewableByForNewReferenceNumberSelectionChanged(selectedViewableBy) {
        const selectedViewableUserTypes = selectedViewableBy.map((selectedViewableBy) => selectedViewableBy.value);
        setNewReferenceNumberViewableBy(selectedViewableUserTypes);
    }
    function onEditableByForNewReferenceNumberSelectionChanged(selectedEditableBy) {
        const selectedEditableUserTypes = selectedEditableBy.map((selectedEditableBy) => selectedEditableBy.value);
        setNewReferenceNumberEditableBy(selectedEditableUserTypes);
    }
    const columns = [
        {
            field: 'name',
            label: 'Name',
            render: (_, row, i) => {
                var _a;
                if (isRowInEdit(row)) {
                    return (React.createElement(TextField, { onChange: handleEditInputChange, value: editReferenceNumberSlug, className: "mx-2" }));
                }
                return React.createElement("div", { className: "ml-5 mt-2" }, t((_a = row === null || row === void 0 ? void 0 : row.slug) === null || _a === void 0 ? void 0 : _a.toUpperCase()));
            },
        },
        {
            field: 'viewable_by',
            label: 'Viewable By',
            render: (_, row, i) => {
                var _a;
                if (isRowInEdit(row)) {
                    return (React.createElement(CustomDropdown, { multiple: true, options: userTypeOptions, value: userTypeOptions.filter((userTypeOption) => editReferenceNumberViewableBy.includes(userTypeOption.value)), onChange: onViewableByForExistingReferenceNumberSelectionChanged, nameKey: "name", valueKey: "value" }));
                }
                return (React.createElement("div", { style: { marginTop: '10px' } }, (_a = row === null || row === void 0 ? void 0 : row.viewable_by) === null || _a === void 0 ? void 0 : _a.join(", ")));
            },
        },
        {
            field: 'editable_by',
            label: 'Editable By',
            render: (_, row, i) => {
                var _a;
                if (isRowInEdit(row)) {
                    return (React.createElement(CustomDropdown, { multiple: true, options: userTypeOptions, value: userTypeOptions.filter((userTypeOption) => editReferenceNumberEditableBy.includes(userTypeOption.value)), onChange: onEditableByForExistingReferenceNumberSelectionChanged, nameKey: "name", valueKey: "value" }));
                }
                return (React.createElement("div", { style: { marginTop: '10px' } }, (_a = row === null || row === void 0 ? void 0 : row.editable_by) === null || _a === void 0 ? void 0 : _a.join(", ")));
            },
        },
        {
            field: 'action',
            label: '',
            render: (_, row, i) => {
                if (isRowInEdit(row)) {
                    return (React.createElement("div", { className: "flex items-center space-x-2 justify-end" },
                        React.createElement(ApproveButton, { onClick: handleFinishEdit, disabled: !canUpdate }),
                        React.createElement(DeclineButton, { color: "gray", onClick: exitEditMode, disabled: isSaving })));
                }
                return (React.createElement("div", { className: "flex justify-end" },
                    React.createElement(Dropdown, { useIcon: true },
                        React.createElement(Dropdown.Button, { onClick: () => enterEditMode(row) }, t('EDIT')),
                        React.createElement(Dropdown.Button, { onClick: () => handleRemove(row) }, titleize(t('REMOVE'))))));
            },
        },
    ];
    const renderFooter = () => {
        return (React.createElement("tr", null,
            React.createElement("td", null,
                React.createElement(TextField, { value: newReferenceNumberSlug, onChange: handleUserInputChangeForNewReferenceNumber })),
            React.createElement("td", null,
                React.createElement(CustomDropdown, { className: "w-full", multiple: true, options: userTypeOptions, value: userTypeOptions.filter((userTypeOption) => newReferenceNumberViewableBy.includes(userTypeOption.value)), onChange: onViewableByForNewReferenceNumberSelectionChanged, nameKey: "name", valueKey: "value", buttonWidth: "w-full" })),
            React.createElement("td", null,
                React.createElement(CustomDropdown, { multiple: true, options: userTypeOptions, value: userTypeOptions.filter((userTypeOption) => newReferenceNumberEditableBy.includes(userTypeOption.value)), onChange: onEditableByForNewReferenceNumberSelectionChanged, nameKey: "name", valueKey: "value", buttonWidth: "w-full" })),
            React.createElement("td", null,
                React.createElement("div", { className: "flex justify-end" },
                    React.createElement(AddButton, { onClick: handleAdd, disabled: !canAdd })))));
    };
    if (isLoading) {
        return (React.createElement("div", { style: {} },
            React.createElement(Spinner, { message: t('LOADING') })));
    }
    return (React.createElement(LayoutWithSidebar, { className: "mt-4 mb-4", title: t('MANAGE_REFERENCE_NUMBERS'), breadcrumbs: breadcrumbs },
        React.createElement("h3", { className: "fs-1 mb-5 ml-2" }, t('MANAGE_REFERENCE_NUMBERS')),
        React.createElement("div", { className: "rounded-lg max-w-5xl shadow ring-1 ring-black ring-opacity-5" },
            React.createElement(Panel, null,
                React.createElement(Table, { columns: columns, footer: renderFooter, data: referenceNumbers, headers: columns }),
                React.createElement(Pagination, { perPage: ITEMS_PER_PAGE_LIMIT, page: activePage, total: totalCount, pages: totalPages, showCount: true, onPageClick: (eventKey) => handlePageChange(eventKey) })))));
};
export default ReferenceNumbersMasterList;
